import { useMemo, useState } from 'react'
import { Screen } from '@/components/layout'
import { DonutChart, Legend, type DonutSlice } from '@/components/charts/DonutChart'
import { MonthPicker } from '@/components/pickers'
import { CategoryRow, List } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonCard, SkeletonList } from '@/components/states'
import type { CategoryId } from '@/generated/types'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { categoryName } from '@/lib/display'
import { formatMoney } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { categoryColorVar } from '@/lib/tokens'
import { useAsync } from '@/lib/useAsync'

/** Spending by category for the selected month, against previous months. */
export function CategoriesScreen() {
  const navigate = useNavigate()
  const cashFlow = useAsync(() => api.analysis.cashFlow(12), [])
  const [selected, setSelected] = useState<string | null>(null)

  const data = cashFlow.state.data
  const months = data?.months ?? []
  const month = months.find((m) => m.month === selected) ?? months.at(-1)

  const rows = useMemo(() => {
    if (!month) return []
    return (month.byCategory ?? [])
      .filter((entry) => entry.category !== 'transfer' && entry.category !== 'savingsTransfer')
      .sort((a, b) => a.amount.amount - b.amount.amount)
  }, [month])

  const trendFor = (category: CategoryId) =>
    months
      .filter((m) => m.complete)
      .slice(-6)
      .map((m) => Math.abs((m.byCategory ?? []).find((c) => c.category === category)?.amount.amount ?? 0))

  const total = rows.reduce((sum, row) => sum + Math.abs(row.amount.amount), 0)
  const currency = data?.currency ?? 'USD'

  const slices: DonutSlice[] = rows.slice(0, 8).map((row) => ({
    key: row.category,
    label: categoryName(row.category),
    value: Math.abs(row.amount.amount),
    color: categoryColorVar(row.category),
  }))

  const screenState =
    cashFlow.state.status === 'error'
      ? 'error'
      : cashFlow.state.status === 'loading' && !data
        ? 'loading'
        : rows.length === 0
          ? 'empty'
          : 'ready'

  return (
    <Screen
      id="categories"
      state={screenState}
      actions={
        months.length > 0 ? (
          <MonthPicker
            months={months.map((m) => m.month)}
            value={month?.month ?? ''}
            onChange={setSelected}
          />
        ) : undefined
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonList rows={8} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="categories.error.title"
          error={cashFlow.state.status === 'error' ? cashFlow.state.error : undefined}
          onRetry={cashFlow.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="categories.empty.title" />
      ) : (
        <div className="stack">
          <div className="card card-pad-lg row" style={{ gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
            <DonutChart
              slices={slices}
              centerLabel={t('categories.total')}
              centerValue={formatMoney({ amount: total, currency }, { absolute: true })}
              formatValue={(value) => formatMoney({ amount: value, currency }, { absolute: true })}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Legend slices={slices} />
            </div>
          </div>

          <List>
            {rows.map((row) => (
              <CategoryRow
                key={row.category}
                category={row.category}
                amount={row.amount}
                change={row.changeVsAverage}
                trend={trendFor(row.category)}
                onSelect={() => navigate('categoryDetail', { id: row.category })}
              />
            ))}
          </List>
        </div>
      )}
    </Screen>
  )
}
